package com.upcominds.expedite.features.puzzle

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.upcominds.expedite.data.challenges.ChallengesRepository
import com.upcominds.expedite.data.mobilelogs.logMobileError
import com.upcominds.expedite.dto.PuzzleAttemptDTO
import com.upcominds.expedite.dto.PuzzleOptionDTO
import com.upcominds.expedite.dto.PuzzleResultDTO
import com.upcominds.expedite.dto.PuzzleUserOptionDTO
import com.upcominds.expedite.model.Challenge
import com.upcominds.expedite.model.QuizTimer
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

sealed interface PuzzleStage {
    data class Start(
        val title: String,
        val description: String,
        val questionCount: Int,
        val challenge: Challenge? = null
    ) : PuzzleStage

    data class Question(
        val questionId: String,
        val questionText: String,
        val options: List<PuzzleUserOptionDTO>,
        val index: Int,
        val totalQuestions: Int,
        val selectedOptionId: String?,
        val isReadOnly: Boolean = false,
        // For review
        val resultOptions: List<PuzzleOptionDTO>? = null,
        val explanation: String? = null,
        val isAnswerCorrect: Boolean? = null
    ) : PuzzleStage

    data class Finished(
        val title: String,
        val result: PuzzleResultDTO,
        val elapsedSeconds: Long
    ) : PuzzleStage
}

data class PuzzleUiState(
    val loading: Boolean = false,
    val stage: PuzzleStage? = null,
    val error: String? = null,
)

class PuzzleViewModel(private val challengesRepository: ChallengesRepository) : ViewModel() {

    private val _uiState = MutableStateFlow(PuzzleUiState())
    val uiState: StateFlow<PuzzleUiState> = _uiState.asStateFlow()

    private var attempt: PuzzleAttemptDTO? = null
    private var challenge: Challenge? = null
    private var answers: MutableMap<String, String> = mutableMapOf() // questionId -> optionId
    private var timer: QuizTimer = QuizTimer()
    private var isCompleted: Boolean = false
    private var puzzleResult: PuzzleResultDTO? = null

    private val challengeCache = mutableMapOf<String, Challenge>()
    private val puzzleAttemptCache = mutableMapOf<String, PuzzleAttemptDTO>()
    private val puzzleResultsCache = mutableMapOf<String, PuzzleResultDTO>()
    private val puzzleTimeCache = mutableMapOf<String, Long>()

    fun loadPuzzle(challengeId: String) {
        if (puzzleResultsCache.containsKey(challengeId)) {
            challenge = challengeCache[challengeId]
            attempt = puzzleAttemptCache[challengeId]
            puzzleResult = puzzleResultsCache[challengeId]
            isCompleted = true

            _uiState.value = _uiState.value.copy(
                loading = false,
                stage = PuzzleStage.Finished(
                    title = challenge?.name ?: "Puzzle Result",
                    result = puzzleResult!!,
                    elapsedSeconds = puzzleTimeCache[challengeId] ?: 0
                )
            )
            return
        }

        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true, error = null)
            try {
                try {
                    challenge = challengesRepository.getChallenge(challengeId)
                    challenge?.let { challengeCache[challengeId] = it }
                } catch (e: Exception) {
                    logMobileError("PuzzleViewModel.loadPuzzle.challenge", e, "Failed to preload challenge $challengeId")
                }

                val puzzleAttempt = challengesRepository.puzzleStart(challengeId)
                this@PuzzleViewModel.attempt = puzzleAttempt
                isCompleted = puzzleAttempt.completed

                if (isCompleted) {
                    finish()
                    return@launch
                }

                answers.clear()
                puzzleAttempt.questions.forEach { question ->
                    question.selectedOptionId?.let {
                        answers[question.id] = it
                    }
                }
                val isStarted = answers.isNotEmpty()

                if (isStarted) {
                    startQuestions()
                } else {
                    _uiState.value = _uiState.value.copy(
                        loading = false,
                        stage = PuzzleStage.Start(
                            title = if (puzzleAttempt.name.isNotBlank()) puzzleAttempt.name else challenge?.name ?: "Puzzle",
                            description = puzzleAttempt.description ?: challenge?.description ?: "Solve the puzzle challenges.",
                            questionCount = puzzleAttempt.questions.size,
                            challenge = challenge
                        )
                    )
                }
            } catch (e: Exception) {
                logMobileError("PuzzleViewModel.loadPuzzle", e, "Failed to load puzzle for challenge $challengeId")
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message ?: "Failed to load puzzle",
                )
            }
        }
    }

    fun startQuestions() {
        timer = QuizTimer().startTimer()
        val firstUnanswered = attempt?.questions?.indexOfFirst { it.id !in answers }?.let { if (it == -1) 0 else it } ?: 0
        showQuestion(firstUnanswered)
    }

    private fun showQuestion(index: Int) {
        val currentAttempt = attempt ?: return
        if (index < 0 || index >= currentAttempt.questions.size) return

        val question = currentAttempt.questions[index]
        val resultAnswer = if (isCompleted) puzzleResult?.answers?.find { it.questionId == question.id } else null

        _uiState.value = _uiState.value.copy(
            loading = false,
            stage = PuzzleStage.Question(
                questionId = question.id,
                questionText = resultAnswer?.questionText ?: question.text,
                options = question.options,
                index = index,
                totalQuestions = currentAttempt.questions.size,
                selectedOptionId = resultAnswer?.selectedOptionId ?: answers[question.id],
                isReadOnly = isCompleted,
                resultOptions = resultAnswer?.options,
                explanation = resultAnswer?.explanation ?: question.explanation,
                isAnswerCorrect = resultAnswer?.isCorrect
            )
        )
    }

    fun selectAnswer(questionId: String, optionId: String) {
        val stage = _uiState.value.stage
        if (stage !is PuzzleStage.Question || stage.selectedOptionId != null || isCompleted) return

        viewModelScope.launch {
            try {
                val currentAttempt = attempt ?: return@launch
                challengesRepository.puzzleSubmitAnswer(currentAttempt.attemptId, questionId, optionId)

                answers[questionId] = optionId

                val currentStage = _uiState.value.stage
                if (currentStage is PuzzleStage.Question) {
                    _uiState.value = _uiState.value.copy(
                        stage = currentStage.copy(selectedOptionId = optionId)
                    )
                }

            } catch (e: Exception) {
                logMobileError("PuzzleViewModel.selectAnswer", e, "Failed to submit puzzle answer for question $questionId")
                _uiState.value = _uiState.value.copy(error = "Failed to submit answer: ${e.message}")
            }
        }
    }

    fun next() {
        val stage = _uiState.value.stage as? PuzzleStage.Question ?: return
        val nextIndex = stage.index + 1
        val currentAttempt = attempt ?: return

        if (nextIndex < currentAttempt.questions.size) {
            showQuestion(nextIndex)
        } else if (!isCompleted) {
            finish()
        }
    }

    fun previous() {
        val stage = _uiState.value.stage as? PuzzleStage.Question ?: return
        val prevIndex = stage.index - 1
        if (prevIndex >= 0) {
            showQuestion(prevIndex)
        }
    }

    fun finish() {
        timer = timer.stopTimer()
        val currentAttempt = attempt ?: return

        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(loading = true)
            try {
                val result = challengesRepository.puzzleComplete(currentAttempt.attemptId)
                isCompleted = true
                puzzleResult = result

                puzzleAttemptCache[currentAttempt.puzzleId] = currentAttempt
                puzzleResultsCache[currentAttempt.puzzleId] = result
                puzzleTimeCache[currentAttempt.puzzleId] = timer.elapsedSeconds.toLong()

                _uiState.value = _uiState.value.copy(
                    loading = false,
                    stage = PuzzleStage.Finished(
                        title = challenge?.name ?: "Puzzle Result",
                        result = result,
                        elapsedSeconds = timer.elapsedSeconds.toLong()
                    )
                )
            } catch (e: Exception) {
                logMobileError("PuzzleViewModel.finish", e, "Failed to complete puzzle attempt ${currentAttempt.attemptId}")
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = "Failed to complete puzzle: ${e.message}"
                )
            }
        }
    }

    fun review() {
        if (!isCompleted || puzzleResult == null) return
        showQuestion(0)
    }

    fun restart() {
        answers.clear()
        attempt = null
        isCompleted = false
        puzzleResult = null
        challenge = null
        clearCaches()
        _uiState.value = PuzzleUiState()
    }

    fun clearCaches() {
        puzzleAttemptCache.clear()
        puzzleResultsCache.clear()
        puzzleTimeCache.clear()
        challengeCache.clear()
    }
}
